/**
 * GENERATED_ASSET — static Hush kit contracts (adopted from the reviewed sol61 batch001 static kit).
 *
 * The kit re-exported these types from the canonical design catalog
 * `docs/plans/2026-10-20-hush-nook-lanternskip/catalog/asset-generator-contracts.ts`. That file is outside
 * this project's `rootDir` (`src/`), so a type import from it does not compile here; the subset this slice
 * needs is copied verbatim instead. Keep these shapes identical to the catalog when either side changes.
 */

export interface Point2 { readonly x: number; readonly y: number }
export interface VisualBounds extends Point2 { readonly width: number; readonly height: number }
export type DetailTier = 'readable' | 'standard' | 'close';
export type PaletteId = 'coastal_salt' | 'nook_warm' | 'icon_ink';
import type { ResolvedPalette } from '../pieces/contract';
export type { PaletteRole, ResolvedPalette } from '../pieces/contract';
export interface StyleProfile {
  readonly outerStroke: number;
  readonly seamStroke: number;
  readonly grainStroke: number;
  readonly grainOpacity: number;
  readonly contactShadowOpacity: number;
  readonly coordinatePrecision: number;
  readonly samplingPadding: number;
}
export interface VisualAnchor {
  readonly name: string;
  readonly point: Point2;
  readonly role: 'origin' | 'attachment' | 'support' | 'effect' | 'inspection';
}
export interface ProtectedVisualRegion {
  readonly name: string;
  readonly role: 'motif' | 'face-clearance' | 'opening' | 'clear-lane';
  readonly bounds: VisualBounds;
  readonly gameplayAuthority: false;
}
export interface ArtifactIdentity {
  readonly assetId: string;
  readonly recipeId: string;
  readonly generatorId: string;
  readonly generatorRevision: string;
  readonly logicalRecipeHash: string;
}
export interface SvgAssetArtifact extends ArtifactIdentity {
  readonly kind: 'svg-artifact';
  readonly svg: string;
  readonly svgBytesHash: string;
  readonly origin: { readonly kind: 'bottom-center' | 'attachment' | 'icon-frame'; readonly point: Point2 };
  readonly contentBounds: VisualBounds;
  readonly paintBounds: VisualBounds;
  readonly viewBox: VisualBounds;
  readonly anchors: readonly VisualAnchor[];
  readonly protectedRegions: readonly ProtectedVisualRegion[];
  readonly renderRole: 'back-structure' | 'dressing' | 'ground-dressing' | 'foreground-edge' | 'ui';
  readonly gameplayGeometry: 'not-generated';
}
interface RecipeEnvelope<G extends string, P> {
  readonly schema: 'hush.asset-recipe.v1';
  readonly id: string;
  readonly assetId: string;
  readonly generatorId: G;
  readonly seed: number;
  readonly paletteId: PaletteId;
  readonly detail: DetailTier;
  readonly params: Readonly<P>;
}
export interface LanternParams {
  readonly condition: 'salt_worn' | 'dry' | 'interior_warm';
  readonly support: 'hanging' | 'standing';
  readonly paperWear: number;
  readonly warmth: number;
  readonly repairNotch: 'lower-left' | 'lower-right' | 'none';
}
export interface RibbonParams {
  readonly pose: 'rest' | 'left_breeze' | 'right_breeze';
  readonly tailLength: number;
  readonly shortTailRatio: number;
  readonly width: number;
  readonly fray: number;
  readonly seam: 'none' | 'short-repair';
}
export interface ReedCurtainParams {
  readonly opening: 'concealed' | 'gathered_open';
  readonly width: number;
  readonly height: number;
  readonly stalksPerSide: number;
  readonly gapWidth: number;
  readonly tieHeightRatio: number;
  readonly tipSpread: number;
}
export interface PaperBoatParams {
  readonly fold: 'classic';
  readonly condition: 'clean' | 'salt_softened';
  readonly clueTab: 'none' | 'two_over_one';
  readonly leanDeg: number;
  readonly paperWear: number;
}
export type LanternRecipe = RecipeEnvelope<'hush.lantern.v1', LanternParams>;
export type RibbonRecipe = RecipeEnvelope<'hush.ribbon.v1', RibbonParams>;
export type ReedCurtainRecipe = RecipeEnvelope<'hush.reeds.v1', ReedCurtainParams>;
export type PaperBoatRecipe = RecipeEnvelope<'hush.paper_boat.v1', PaperBoatParams>;

// ---------------------------------------------------------------- kit-local types ----------------------------------

/**
 * Lantern, ribbon, reed and paper-boat families. The paper boat backs the Hush wardrobe's held prop only;
 * the `paper_boat` map template still draws its own hand-drawn art.
 */
export type StaticRecipe = LanternRecipe | RibbonRecipe | ReedCurtainRecipe | PaperBoatRecipe;

export interface DraftLayer {
  readonly id: string;
  readonly renderRole: SvgAssetArtifact['renderRole'];
  readonly body: string;
}

export type DraftArtifact = Omit<SvgAssetArtifact, 'logicalRecipeHash' | 'svgBytesHash'> & {
  readonly claim: 'CANDIDATE_NOT_INTEGRATED';
  readonly defs: string;
  readonly body: string;
  readonly layers: readonly DraftLayer[];
  readonly geometry: Readonly<Record<string, unknown>>;
  readonly boundsMethod: 'control-hull-and-stroke' | 'existing-source-frame';
};

export interface DetailTierTokens { readonly grainMarks: number; readonly minorRepairs: boolean }

export interface Tokens {
  readonly revision: string;
  readonly palettes: Readonly<Record<string, ResolvedPalette>>;
  readonly style: StyleProfile;
  readonly detailTiers: Readonly<Record<string, DetailTierTokens>>;
  readonly masterMotif: {
    readonly id: string;
    readonly centers: readonly Point2[];
    readonly radius: number;
    readonly protectedRadius: number;
    readonly randomization: string;
  };
}

/** Generator revision of the frozen batch001 kit (`static-kit/source/contracts.ts`). */
export const REVISION = 'hush-static-candidate.2';
